import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type TouchEvent,
} from 'react';
import './scroll.css';

/**
 * Four chart cards, one open wide and the rest folded to strips.
 *
 * Every number and curve in here is drawn, not measured. Krevya has no
 * customer results to publish, and the line under the row says so once. It
 * used to say so five times over: in every card title, on a badge inside
 * every card, and in the lead paragraph as well, which turned the section
 * that answers "where will I see this work?" into a disclaimer with charts
 * attached. One statement, where a reader looking for the catch will find
 * it, is honest; five is the section apologising for itself.
 *
 * On a desktop the open card takes the row and the others keep a 44px strip
 * each; a click, a resting pointer, the arrow buttons or the arrow keys switch
 * it. The cards' contents are laid out at the open width from the start and
 * only revealed as the column widens, so a chart never reflows mid-animation.
 * A phone has the same accordion with narrower strips, and a sideways swipe
 * across the row turns it as the arrows do.
 *
 * Lines draw themselves (stroke-dashoffset) and bars grow when the section is
 * on screen and the card is open; the CSS owns the timing, so a card that
 * closes resets and draws again the next time it opens.
 *
 * Pointing at a chart reads it: a guide at the nearest point, a dot on each
 * series and a label with the values there. The values are the plotted
 * heights put through the card's `scale`, which is pinned so the last point
 * reads exactly what the card's headline tile says; a readout that disagreed
 * with the number above it would undo the card. A mouse reads by hovering.
 * A finger reads by tapping, and the reading stays until the next tap
 * somewhere else; a finger that moves is the phone carousel's swipe, and the
 * chart lets it go without claiming it.
 */

type Tx = (en: string, tr: string) => string;

const PHONE = '(max-width: 760px)';
const HOVER_OPEN_MS = 160;

type Tile = { label: string; value: string; tone?: 'a' | 'b' };
type Plot =
  | { kind: 'line'; series: number[][]; area: boolean; start: number }
  | { kind: 'bars'; series: [number[], number[]]; start: number };
/**
 * What a plotted height means, one to a series: a height `v` reads
 * `min + v * (max - min)`, written by `format` in the reader's language.
 */
type Scale = { min: number; max: number; format: (value: number) => string };

/* Illustrative shapes: flat, then a turn at `start`. Values are 0..1 heights. */
const AD_LINE = [0.22, 0.25, 0.21, 0.24, 0.2, 0.23, 0.22, 0.26, 0.24, 0.27, 0.33, 0.38, 0.36, 0.45, 0.5, 0.48, 0.58, 0.63, 0.61, 0.72, 0.78, 0.76, 0.86, 0.9];
const SEARCH_SEEN = [0.32, 0.34, 0.3, 0.33, 0.31, 0.35, 0.33, 0.32, 0.36, 0.42, 0.48, 0.54, 0.52, 0.62, 0.68, 0.66, 0.76, 0.82, 0.8, 0.9];
const SEARCH_CLICKED = [0.1, 0.12, 0.1, 0.11, 0.13, 0.11, 0.13, 0.12, 0.13, 0.17, 0.21, 0.25, 0.24, 0.31, 0.35, 0.34, 0.41, 0.46, 0.45, 0.53];
const MAIL_CAMPAIGN = [0.2, 0.22, 0.19, 0.23, 0.21, 0.28, 0.33, 0.37, 0.42, 0.46, 0.52, 0.57];
const MAIL_FLOWS = [0.08, 0.07, 0.09, 0.08, 0.09, 0.13, 0.16, 0.19, 0.22, 0.25, 0.28, 0.31];
const FOLLOWERS = [0.12, 0.13, 0.13, 0.14, 0.15, 0.15, 0.16, 0.17, 0.2, 0.24, 0.29, 0.35, 0.41, 0.48, 0.55, 0.62, 0.69, 0.75, 0.82, 0.88];

/**
 * The top of a scale that starts at `min` and puts the series' last point on
 * `end`, the figure the card's tile shows. The bottom is picked by hand so the
 * first points read as a plausible "before".
 */
function topFor(series: number[], min: number, end: number): number {
  return min + (end - min) / series[series.length - 1]!;
}
const decimal = (value: number, digits: number, comma: boolean) => {
  const text = value.toFixed(digits);
  return comma ? text.replace('.', ',') : text;
};

export function ChartsRow({ tx }: { tx: Tx }) {
  const [open, setOpen] = useState(0);
  const row = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef(0);

  // 48.2K and 3.1K in English, 48,2 B and 3,1 B in Turkish, as the tiles write them.
  const count1k = (v: number) =>
    v >= 1000 ? tx(`${decimal(v / 1000, 1, false)}K`, `${decimal(v / 1000, 1, true)} B`) : String(Math.round(v));
  const times = (v: number) => tx(`${decimal(v, 1, false)}×`, `${decimal(v, 1, true)}×`);
  const change = (v: number) => {
    const n = Math.round(v);
    if (n === 0) return tx('0%', '%0');
    const sign = n > 0 ? '+' : '−';
    return tx(`${sign}${Math.abs(n)}%`, `${sign}%${Math.abs(n)}`);
  };
  // The ad, search and follower charts run by the week; the email bars are a year of months.
  const week = (i: number) => tx(`Week ${i + 1}`, `${i + 1}. hafta`);
  const month = (i: number) => tx(`Month ${i + 1}`, `${i + 1}. ay`);

  const cards: {
    key: string;
    title: string;
    tiles: Tile[];
    plot: Plot;
    scale: Scale[];
    legend?: [string, string];
    period: (index: number) => string;
  }[] = [
    {
      key: 'ads',
      title: tx('Ad performance', 'Reklam performansı'),
      tiles: [
        { label: tx('Return on ad spend', 'Reklam getirisi'), value: tx('3.4×', '3,4×') },
        { label: tx('Click-through rate', 'Tıklama oranı'), value: tx('2.8%', '%2,8') },
        { label: tx('Cost per purchase', 'Satış başı maliyet'), value: tx('−27%', '−%27') },
      ],
      plot: { kind: 'line', series: [AD_LINE], area: true, start: 9 },
      // Return on ad spend: about 1.3× before, 3.4× at the end.
      scale: [{ min: 0.6, max: topFor(AD_LINE, 0.6, 3.4), format: times }],
      period: week,
    },
    {
      key: 'search',
      title: tx('Search visibility', 'Arama görünürlüğü'),
      tiles: [
        { label: tx('Impressions', 'Gösterim'), value: tx('48.2K', '48,2 B'), tone: 'a' },
        { label: tx('Clicks', 'Tıklama'), value: tx('3.1K', '3,1 B'), tone: 'b' },
        { label: tx('Average position', 'Ortalama sıra'), value: tx('8.4', '8,4') },
      ],
      plot: { kind: 'line', series: [SEARCH_SEEN, SEARCH_CLICKED], area: false, start: 8 },
      // Impressions from about 17K to 48.2K; clicks from about 590 to 3.1K.
      scale: [
        { min: 0, max: topFor(SEARCH_SEEN, 0, 48_200), format: count1k },
        { min: 0, max: topFor(SEARCH_CLICKED, 0, 3_100), format: count1k },
      ],
      period: week,
    },
    {
      key: 'mail',
      title: tx('Email revenue', 'E-posta geliri'),
      tiles: [
        { label: tx('Campaigns', 'Kampanyalar'), value: tx('+38%', '+%38'), tone: 'a' },
        { label: tx('Automations', 'Otomasyonlar'), value: tx('+55%', '+%55'), tone: 'b' },
        { label: tx('Total', 'Toplam'), value: tx('+44%', '+%44') },
      ],
      plot: { kind: 'bars', series: [MAIL_CAMPAIGN, MAIL_FLOWS], start: 5 },
      /*
       * The tiles are growth, so each segment reads as its revenue's change:
       * within a few points of 0% in the months before, +38% and +55% in the last.
       */
      scale: [
        { min: -20, max: topFor(MAIL_CAMPAIGN, -20, 38), format: change },
        { min: -19, max: topFor(MAIL_FLOWS, -19, 55), format: change },
      ],
      period: month,
    },
    {
      key: 'followers',
      title: tx('Follower growth', 'Takipçi büyümesi'),
      tiles: [
        { label: tx('Followers', 'Takipçi'), value: tx('12.4K', '12,4 B') },
        { label: tx('Engagement', 'Etkileşim'), value: tx('4.1%', '%4,1') },
        { label: tx('Shares', 'Paylaşım'), value: '860' },
      ],
      plot: { kind: 'line', series: [FOLLOWERS], area: true, start: 7 },
      // Followers from about 1.7K to 12.4K.
      scale: [{ min: 0, max: topFor(FOLLOWERS, 0, 12_400), format: count1k }],
      period: week,
    },
  ];

  const count = cards.length;

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const go = (index: number) => setOpen((index + count) % count);

  /*
   * A sideways swipe on a phone turns the accordion one card. Only a gesture
   * that travels well over 40px and more sideways than down counts, so a
   * scroll down the page or a tap on a chart to read a value never does.
   * Touch events, passive: nothing here stops the page from scrolling.
   */
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    swipe.current = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const start = swipe.current;
    const touch = event.changedTouches[0];
    swipe.current = null;
    if (!start || !touch || !window.matchMedia(PHONE).matches) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    go(open + (dx < 0 ? 1 : -1));
  };

  const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + count) % count;
    go(next);
    row.current?.querySelectorAll<HTMLButtonElement>('.lp-chart-tab')[next]?.focus();
  };

  // A pointer resting on a strip opens it; one only passing over does not.
  const hoverOpen = (index: number) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia(PHONE).matches) return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpen(index), HOVER_OPEN_MS);
  };
  const hoverCancel = () => window.clearTimeout(hoverTimer.current);

  const marker = tx('Started with Krevya', 'Krevya ile başladı');

  return (
    <section className="lp-charts lp-staged" id="examples" data-reveal>
      <header data-stage style={{ '--i': 0 } as CSSProperties}>
        <h2>{tx('Where you’ll watch it land.', 'Sonucu nerede izleyeceksin?')}</h2>
        <p>
          {tx(
            'Krevya makes the angles, the ads and the page. These are the four places a store watches for what changes next.',
            'Krevya açıları, reklamları ve sayfayı hazırlar. Sonrasında ne değiştiğini bir mağaza bu dört yerden izler.',
          )}
        </p>
      </header>
      <div
        ref={row}
        className="lp-charts-row"
        data-stage
        onPointerLeave={hoverCancel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {cards.map((card, i) => (
          <article
            className="lp-chart-card"
            data-open={i === open ? 'true' : 'false'}
            data-kind={card.plot.kind}
            key={card.key}
            onPointerEnter={() => (i === open ? hoverCancel() : hoverOpen(i))}
          >
            <h3 className="lp-chart-heading">
              <button
                type="button"
                className="lp-chart-tab"
                id={`lp-chart-tab-${card.key}`}
                aria-expanded={i === open}
                aria-controls={`lp-chart-${card.key}`}
                onClick={() => go(i)}
                onKeyDown={(event) => onKey(event, i)}
              >
                <span>{card.title}</span>
              </button>
            </h3>
            <div
              className="lp-chart-body"
              id={`lp-chart-${card.key}`}
              role="region"
              aria-labelledby={`lp-chart-tab-${card.key}`}
            >
              <dl className="lp-chart-tiles">
                {card.tiles.map((tile) => (
                  <div className="lp-chart-tile" data-tone={tile.tone} key={tile.label}>
                    <dt>
                      {tile.tone ? <i aria-hidden="true" /> : null}
                      {tile.label}
                    </dt>
                    <dd>{tile.value}</dd>
                  </div>
                ))}
              </dl>
              <Chart
                plot={card.plot}
                marker={marker}
                scale={card.scale}
                names={seriesNames(card.tiles)}
                period={card.period}
              />
            </div>
          </article>
        ))}
      </div>
      <div className="lp-charts-foot" data-stage style={{ '--i': 2 } as CSSProperties}>
        <p>
          {tx(
            'Charts are illustrative, not real customer results.',
            'Grafikler örnek amaçlıdır; gerçek müşteri sonucu değildir.',
          )}
        </p>
        <div className="lp-charts-arrows">
          <button type="button" onClick={() => go(open - 1)} aria-label={tx('Previous chart', 'Önceki grafik')}>
            <Chevron back />
          </button>
          <span aria-live="polite">{cards[open]?.title}</span>
          <button type="button" onClick={() => go(open + 1)} aria-label={tx('Next chart', 'Sonraki grafik')}>
            <Chevron />
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * The readout's name for each series. A two-series card's coloured tiles are
 * its legend, in series order; a one-series card plots its first tile.
 */
function seriesNames(tiles: Tile[]): string[] {
  const toned = tiles.filter((tile) => tile.tone).map((tile) => tile.label);
  return toned.length > 0 ? toned : tiles.slice(0, 1).map((tile) => tile.label);
}

function Chevron({ back = false }: { back?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d={back ? 'M14.5 6 8.5 12l6 6' : 'M9.5 6l6 6-6 6'} />
    </svg>
  );
}

/* ── The chart ─────────────────────────────────────────────────────────── */

/*
 * Drawn at the box's real pixel size rather than stretched from a fixed
 * viewBox: a stretched SVG thickens its strokes unevenly, and the line-drawing
 * reveal needs a path whose length is the one on screen.
 */
function useBox() {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      setBox((old) => (old.w === w && old.h === h ? old : { w, h }));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, box] as const;
}

// Room above the plot for the marker pill, and a hair at the edges for the stroke.
const PAD = { t: 64, r: 6, b: 10, l: 6 };

/** A smooth path through the points (Catmull-Rom, drawn as cubic Béziers). */
function smooth(points: [number, number][]): string {
  if (points.length === 0) return '';
  const f = (n: number) => n.toFixed(1);
  let d = `M${f(points[0]![0])},${f(points[0]![1])}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const c1: [number, number] = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2: [number, number] = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${f(c1[0])},${f(c1[1])} ${f(c2[0])},${f(c2[1])} ${f(p2[0])},${f(p2[1])}`;
  }
  return d;
}

/** How far a finger may move and still count as a tap rather than a swipe. */
const TAP_SLOP = 10;

function Chart({
  plot,
  marker,
  scale,
  names,
  period,
}: {
  plot: Plot;
  marker: string;
  scale: Scale[];
  names: string[];
  period: (index: number) => string;
}) {
  const [ref, { w, h }] = useBox();
  // The point being read, and whether a mouse (gone when it leaves) or a tap (kept) chose it.
  const [reading, setReading] = useState<{ index: number; by: 'mouse' | 'tap' } | null>(null);
  const press = useRef<{ id: number; x: number; y: number } | null>(null);
  const label = useRef<HTMLDivElement>(null);
  const [labelBox, setLabelBox] = useState({ w: 0, h: 0 });
  // The pill's own width, so it can be kept inside the card whatever the language makes it.
  const pill = useRef<HTMLSpanElement>(null);
  const [pillW, setPillW] = useState(0);
  useLayoutEffect(() => {
    if (pill.current) setPillW(pill.current.offsetWidth);
  }, [marker, w]);
  const innerW = Math.max(0, w - PAD.l - PAD.r);
  const innerH = Math.max(0, h - PAD.t - PAD.b);
  const base = h - PAD.b;
  const yOf = (v: number) => base - v * innerH;
  const grid = [0.25, 0.5, 0.75, 1].map((g) => yOf(g));

  let body: ReactNode = null;
  // How many points there are, and where point `i` sits: its x, and each series' height and y there.
  let points = 0;
  let place: (i: number) => { x: number; values: number[]; ys: number[] } = () => ({ x: 0, values: [], ys: [] });
  let markX = 0;
  let markY = 0;
  // A line chart's stem runs to the axis; a bar chart's stops at the dot, so
  // it does not cut down through the bar it points at.
  let stemEnd = base;

  if (w > 0 && h > 0 && plot.kind === 'line') {
    const n = plot.series[0]!.length;
    const xOf = (i: number) => PAD.l + (i / (n - 1)) * innerW;
    const lines = plot.series.map((s) => smooth(s.map((v, i) => [xOf(i), yOf(v)])));
    markX = xOf(plot.start);
    markY = yOf(plot.series[0]![plot.start]!);
    points = n;
    place = (i) => {
      const values = plot.series.map((s) => s[i]!);
      return { x: xOf(i), values, ys: values.map(yOf) };
    };
    body = (
      <>
        {plot.area ? (
          <path className="lp-chart-area" d={`${lines[0]} L${xOf(n - 1).toFixed(1)},${base} L${PAD.l},${base} Z`} />
        ) : null}
        {lines.map((d, i) => (
          <path key={i} className="lp-chart-line" data-series={i} d={d} pathLength={1} />
        ))}
      </>
    );
  } else if (w > 0 && h > 0 && plot.kind === 'bars') {
    const [lower, upper] = plot.series;
    const n = lower.length;
    const slot = innerW / n;
    const bar = Math.min(34, slot * 0.58);
    // On the top of the first bar Krevya ran, at its centre: the same x the
    // bar is drawn at below. It used to sit in the gap before that bar at the
    // previous bar's height, which read as a dot floating beside the chart.
    markX = PAD.l + slot * plot.start + slot / 2;
    markY = yOf(lower[plot.start]! + upper[plot.start]!);
    stemEnd = markY - 5;
    points = n;
    // A dot on the top of each segment of the bar.
    place = (i) => {
      const values = [lower[i]!, upper[i]!];
      return { x: PAD.l + slot * i + slot / 2, values, ys: [yOf(values[0]!), yOf(values[0]! + values[1]!)] };
    };
    body = (
      <>
        {lower.map((low, i) => {
          const x = PAD.l + slot * i + (slot - bar) / 2;
          const top = upper[i]!;
          return (
            <g className="lp-chart-bar" key={i} style={{ '--i': i } as CSSProperties}>
              <rect x={x} y={yOf(low)} width={bar} height={low * innerH} rx={3} />
              <rect data-series={1} x={x} y={yOf(low + top)} width={bar} height={Math.max(0, top * innerH - 2)} rx={3} />
            </g>
          );
        })}
      </>
    );
  }

  // The nearest point to an x across the box: the nearest line point, or the bar the x is over.
  const indexAt = (clientX: number) => {
    const el = ref.current;
    if (!el || points === 0 || innerW <= 0) return null;
    const t = (clientX - el.getBoundingClientRect().left - PAD.l) / innerW;
    const i = plot.kind === 'line' ? Math.round(t * (points - 1)) : Math.floor(t * points);
    return Math.min(points - 1, Math.max(0, i));
  };

  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse') {
      const index = indexAt(event.clientX);
      if (index !== null) {
        setReading((old) => (old?.index === index && old.by === 'mouse' ? old : { index, by: 'mouse' }));
      }
      return;
    }
    // A finger that travels is a swipe or a scroll: not a tap, and not ours.
    const start = press.current;
    if (start && start.id === event.pointerId && Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_SLOP) {
      press.current = null;
    }
  };
  const onDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse') return;
    press.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };
  const onUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = press.current;
    press.current = null;
    if (event.pointerType === 'mouse' || !start || start.id !== event.pointerId) return;
    const index = indexAt(event.clientX);
    if (index !== null) setReading({ index, by: 'tap' });
  };
  // The browser took the gesture for the carousel or the page.
  const onCancel = () => {
    press.current = null;
  };
  const onLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse') setReading((old) => (old?.by === 'mouse' ? null : old));
  };

  // A tapped reading stays until the next tap lands outside this chart.
  const tapped = reading?.by === 'tap';
  useEffect(() => {
    if (!tapped) return;
    const onAnyDown = (event: globalThis.PointerEvent) => {
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      setReading(null);
    };
    document.addEventListener('pointerdown', onAnyDown, true);
    return () => document.removeEventListener('pointerdown', onAnyDown, true);
  }, [tapped, ref]);

  const at = reading && body && points > 0 ? place(Math.min(reading.index, points - 1)) : null;
  const atIndex = reading ? Math.min(reading.index, Math.max(0, points - 1)) : 0;

  // The label's own size, so it can sit beside the guide and still stay inside the card.
  useLayoutEffect(() => {
    const el = label.current;
    if (!el) return;
    const next = { w: el.offsetWidth, h: el.offsetHeight };
    setLabelBox((old) => (old.w === next.w && old.h === next.h ? old : next));
  }, [reading, w, h, period, names]);

  let readout: ReactNode = null;
  let labelX = 0;
  let labelY = 0;
  if (at) {
    // To the right of the guide, or to its left when the right would run out of the card.
    const gap = 12;
    labelX = at.x + gap + labelBox.w <= w ? at.x + gap : Math.max(0, at.x - gap - labelBox.w);
    // Level with the highest dot, kept inside the box.
    const top = Math.min(...at.ys);
    labelY = Math.min(Math.max(0, top - labelBox.h / 2), Math.max(0, h - labelBox.h));
    readout = (
      <g className="lp-chart-readout-marks">
        <line
          className="lp-chart-guide"
          x1={0}
          x2={0}
          y1={PAD.t - 8}
          y2={base}
          style={{ transform: `translateX(${at.x.toFixed(1)}px)` }}
        />
        {at.ys.map((y, k) => (
          <circle
            key={k}
            className="lp-chart-dot"
            data-series={k}
            r={4.5}
            style={{ transform: `translate(${at.x.toFixed(1)}px, ${y.toFixed(1)}px)` }}
          />
        ))}
      </g>
    );
  }

  return (
    <div
      className="lp-chart"
      ref={ref}
      onPointerMove={onMove}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      onPointerLeave={onLeave}
    >
      {body ? (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" focusable="false">
          {grid.map((y, i) => (
            <line key={i} className="lp-chart-grid" x1={PAD.l} x2={w - PAD.r} y1={y} y2={y} />
          ))}
          {body}
          <g className="lp-chart-mark">
            <line x1={markX} x2={markX} y1={PAD.t - 18} y2={stemEnd} />
            <circle cx={markX} cy={markY} r={5} />
          </g>
          {readout}
        </svg>
      ) : null}
      {at ? (
        <div
          ref={label}
          className="lp-chart-readout"
          style={{ transform: `translate(${labelX.toFixed(1)}px, ${labelY.toFixed(1)}px)` }}
        >
          <span className="lp-chart-readout-at">{period(atIndex)}</span>
          {at.values.map((v, k) => {
            const s = scale[k];
            if (!s) return null;
            return (
              <span className="lp-chart-readout-row" data-series={k} key={k}>
                <i aria-hidden="true" />
                <span>{names[k]}</span>
                <b>{s.format(s.min + v * (s.max - s.min))}</b>
              </span>
            );
          })}
        </div>
      ) : null}
      {body ? (
        <span
          ref={pill}
          className="lp-chart-pill"
          // Centred over the point, then pushed back inside the card when a longer
          // label (English "Started with Krevya") would run past either edge.
          style={{ '--x': `${Math.min(Math.max(markX - pillW / 2, 0), Math.max(0, w - pillW))}px` } as CSSProperties}
        >
          <i aria-hidden="true" />
          {marker}
        </span>
      ) : null}
    </div>
  );
}
